import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardFooter, CardHeader } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { StatusPill } from '../components/StatusPill.js'
import { Icon } from '../components/Icon.js'
import {
  StudentForm,
  type StudentFormValues,
  type HistoryDraft,
} from '../components/StudentForm.js'
import { StudentParentSection } from '../components/StudentParentSection.js'
import { FeePlanPanel } from '../components/FeePlanPanel.js'
import { StudentFeesPanel } from '../components/StudentFeesPanel.js'
import { StudentExamsPanel } from '../components/StudentExamsPanel.js'
import { type ParentLink, missingStudentFields } from '../lib/studentTypes.js'

export const Route = createFileRoute('/_app/students/$id')({
  component: StudentEditPage,
})

interface PreviousMark {
  subject: string
  percentage: number
  proofUrl?: string | null
}

interface ClassRef {
  id: string
  name: string
}

interface ClassRow extends ClassRef {
  academicYear: string | null
  isActive: boolean
}

interface StudentDetail {
  student: {
    id: string
    fullName: string
    dateOfBirth: string | null
    schoolName: string | null
    address: string | null
    examBoard: string | null
    photoUrl: string | null
    specialNotes: string | null
    previousMarks: PreviousMark[] | null
    isActive: boolean
    createdAt: string
  }
  parents: ParentLink[]
  classes: ClassRef[]
}

interface StudentListRow {
  id: string
  fullName: string
  classes: ClassRef[]
  examBoard: string | null
  schoolName: string | null
  isActive: boolean
  deletedAt: string | null
  createdAt: string
}

function StudentEditPage() {
  const { id } = Route.useParams()
  const qc = useQueryClient()

  const detail = useQuery({
    queryKey: ['students', id],
    queryFn: async () => {
      const res = await api<StudentDetail>(`/api/v1/students/${id}`)
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const classesQuery = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [values, setValues] = useState<StudentFormValues>({
    fullName: '',
    dateOfBirth: '',
    schoolName: '',
    examBoard: '',
    address: '',
    photoUrl: null,
    specialNotes: '',
  })
  const [selectedClassIds, setSelectedClassIds] = useState<Set<string>>(new Set())
  const [history, setHistory] = useState<HistoryDraft[]>([])

  const [saved, setSaved] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Seed form state from fetched data
  useEffect(() => {
    if (!detail.data) return
    const { student, classes } = detail.data
    setValues({
      fullName: student.fullName,
      dateOfBirth: student.dateOfBirth ?? '',
      schoolName: student.schoolName ?? '',
      examBoard: (student.examBoard as StudentFormValues['examBoard']) ?? '',
      address: student.address ?? '',
      photoUrl: student.photoUrl,
      specialNotes: student.specialNotes ?? '',
    })
    setSelectedClassIds(new Set(classes.map((c) => c.id)))
    setHistory(
      (student.previousMarks ?? []).map((m) => ({
        subject: m.subject,
        percentage: String(m.percentage),
        proofUrl: m.proofUrl ?? null,
      })),
    )
  }, [detail.data])

  const toggleClass = (classId: string) => {
    setSelectedClassIds((curr) => {
      const next = new Set(curr)
      if (next.has(classId)) next.delete(classId)
      else next.add(classId)
      return next
    })
  }

  const save = useMutation({
    mutationFn: async () => {
      const previousMarks = history
        .filter((h) => h.subject.trim() && h.percentage !== '')
        .map((h) => ({
          subject: h.subject.trim(),
          percentage: parseFloat(h.percentage),
          proofUrl: h.proofUrl ?? undefined,
        }))

      const res = await api<{ student: StudentDetail['student']; classes: ClassRef[] }>(
        `/api/v1/students/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            fullName: values.fullName,
            classIds: Array.from(selectedClassIds),
            dateOfBirth: values.dateOfBirth || undefined,
            schoolName: values.schoolName || undefined,
            examBoard: values.examBoard || undefined,
            address: values.address || undefined,
            photoUrl: values.photoUrl ?? undefined,
            specialNotes: values.specialNotes.trim() || undefined,
            previousMarks: previousMarks.length > 0 ? previousMarks : [],
          }),
        },
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (updated) => {
      // Seed detail cache with the returned student + classes
      qc.setQueryData<StudentDetail>(['students', id], (cache) => {
        if (!cache) return cache
        return {
          ...cache,
          student: updated.student,
          classes: updated.classes,
        }
      })
      // Merge into the list cache as well
      const mergedRow: Partial<StudentListRow> & { id: string } = {
        ...updated.student,
        classes: updated.classes,
      }
      qc.setQueryData<StudentListRow[]>(['students'], (rows) =>
        (rows ?? []).map((s) => (s.id === id ? { ...s, ...mergedRow } : s)),
      )
      setSaved('Changes saved')
      setSaveError(null)
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Failed to save')
      setSaved(null)
    },
  })

  const toggleActive = useMutation({
    mutationFn: async (isActive: boolean) => {
      const res = await api<{ student: StudentDetail['student']; classes: ClassRef[] }>(
        `/api/v1/students/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ isActive }),
        },
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (updated) => {
      qc.setQueryData<StudentDetail>(['students', id], (cache) => {
        if (!cache) return cache
        return { ...cache, student: updated.student, classes: updated.classes }
      })
      qc.setQueryData<StudentListRow[]>(['students'], (rows) =>
        (rows ?? []).map((s) =>
          s.id === id ? { ...s, ...updated.student, classes: updated.classes } : s,
        ),
      )
      setSaved(updated.student.isActive ? 'Student reactivated' : 'Student deactivated')
      setSaveError(null)
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Failed')
      setSaved(null)
    },
  })

  if (detail.isLoading) {
    return <p className="text-sm text-fg-subtle">Loading...</p>
  }
  if (detail.error || !detail.data?.student) {
    return (
      <p className="text-sm text-danger">
        {detail.error instanceof Error ? detail.error.message : 'Failed to load student'}
      </p>
    )
  }

  const { student, parents } = detail.data

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setSaved(null)
    setSaveError(null)
    const missing = missingStudentFields(values, selectedClassIds)
    if (missing) {
      setSaveError(missing)
      return
    }
    save.mutate()
  }

  return (
    <div className="max-w-3xl space-y-8">
      <PageHeader
        title={values.fullName || student.fullName}
        description={
          <Link to="/students" className="hover:text-fg">
            Back to all students
          </Link>
        }
        actions={
          <div className="flex items-center gap-3">
            <StatusPill tone={student.isActive ? 'active' : 'cancelled'}>
              {student.isActive ? 'Active' : 'Inactive'}
            </StatusPill>
            {student.isActive ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={toggleActive.isPending}
                onClick={() => {
                  if (confirm(`Mark ${student.fullName} inactive? They can be reactivated later.`)) {
                    toggleActive.mutate(false)
                  }
                }}
              >
                Deactivate
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={toggleActive.isPending}
                onClick={() => toggleActive.mutate(true)}
              >
                Reactivate
              </Button>
            )}
          </div>
        }
      />

      {saved || saveError ? (
        <div
          className={`flex items-start gap-2 rounded-md border px-4 py-3 text-sm ${
            saveError
              ? 'border-danger-border bg-danger-tint text-danger'
              : 'border-success-border bg-success-tint text-success'
          }`}
        >
          <Icon name={saveError ? 'alert' : 'check'} size={16} className="mt-0.5" />
          <span>{saveError ?? saved}</span>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-6">
        <StudentForm
          values={values}
          onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
          classes={classesQuery.data ?? []}
          selectedClassIds={selectedClassIds}
          onToggleClass={toggleClass}
          history={history}
          onHistoryChange={setHistory}
        />

        <Card>
          <CardHeader
            title="Save changes"
            description={`Last updated: ${new Date(student.createdAt).toLocaleDateString('en-IN')}`}
          />
          <CardFooter className="flex items-center justify-end gap-3">
            <Button type="submit" loading={save.isPending}>
              Save changes
            </Button>
          </CardFooter>
        </Card>
      </form>

      <StudentParentSection
        mode="manage"
        studentId={id}
        parents={parents}
      />

      <StudentFeesPanel studentId={id} />

      <FeePlanPanel studentId={id} />

      <StudentExamsPanel studentId={id} />
    </div>
  )
}
